import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Chip,
  Divider,
} from '@mui/material';
import { Upload, CloudUpload, Check, Error as ErrorIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../services/api';
import type { ImportOpportunityRow, ImportOpportunitiesResult } from '../types';

interface CsvRow {
  [key: string]: string;
}

// Columnas exactas de Monday.com para Ventas
interface ColumnMapping {
  title?: string;              // Nombre
  tasks?: string;              // Tareas
  owner?: string;              // Resp.
  type?: string;               // Tipo de Opps
  customerName?: string;       // Cliente
  state?: string;              // Etapas
  estimatedValue?: string;     // Valor del acuerdo
  finalInvoiceAmount?: string; // Factura Final
  probabilityPct?: string;     // Probabilidad
  offerReference?: string;     // Oferta/ Factura/ REMITO
  receiptNumber?: string;      // Comprobante
  estimatedCloseDate?: string; // Fecha de Cierre
  deliverySlipReference?: string; // Remito DE ENTREGA
  deliveryCondition?: string;  // Condicion Delivery
  deliveryStatus?: string;     // Estado Delivery
  receivedConfirmation?: string; // Recibido
  deliveryInfo?: string;       // Info Entrega
  paymentStatus?: string;      // Pago
  paymentMethod?: string;      // Condicion Pago
  isReconciled?: string;       // Conciliado
  paidAmount?: string;         // Abono ($)
  balance?: string;            // Saldo
  actualDeliveryDate?: string; // Fecha de entrega
  paymentDueDate?: string;     // Fecha VTO
  paymentDate?: string;        // Fecha Abono
  daysOverdue?: string;        // Días de Atraso
  commissionPct?: string;      // Comisión
  commissionAmount?: string;   // Monto Comisión
  documentUrl?: string;        // monday Doc v2
}

const fieldLabels: Record<keyof ColumnMapping, string> = {
  title: 'Nombre *',
  tasks: 'Tareas',
  owner: 'Resp.',
  type: 'Tipo de Opps',
  customerName: 'Cliente',
  state: 'Etapas',
  estimatedValue: 'Valor del acuerdo',
  finalInvoiceAmount: 'Factura Final',
  probabilityPct: 'Probabilidad',
  offerReference: 'Oferta/ Factura/ REMITO',
  receiptNumber: 'Comprobante',
  estimatedCloseDate: 'Fecha de Cierre',
  deliverySlipReference: 'Remito DE ENTREGA',
  deliveryCondition: 'Condicion Delivery',
  deliveryStatus: 'Estado Delivery',
  receivedConfirmation: 'Recibido',
  deliveryInfo: 'Info Entrega',
  paymentStatus: 'Pago',
  paymentMethod: 'Condicion Pago',
  isReconciled: 'Conciliado',
  paidAmount: 'Abono ($)',
  balance: 'Saldo',
  actualDeliveryDate: 'Fecha de entrega',
  paymentDueDate: 'Fecha VTO',
  paymentDate: 'Fecha Abono',
  daysOverdue: 'Días de Atraso',
  commissionPct: 'Comisión',
  commissionAmount: 'Monto Comisión',
  documentUrl: 'monday Doc v2',
};

// Convert Excel serial date to ISO string
const excelDateToISO = (excelDate: number | string): string | undefined => {
  if (typeof excelDate === 'string') {
    const d = new Date(excelDate);
    if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
    return undefined;
  }
  if (typeof excelDate === 'number' && excelDate > 40000 && excelDate < 60000) {
    const date = new Date((excelDate - 25569) * 86400 * 1000);
    return date.toISOString().split('T')[0];
  }
  return undefined;
};

export default function ImportOpportunitiesPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'upload' | 'mapping' | 'preview' | 'importing' | 'result'>('upload');
  const [csvData, setCsvData] = useState<CsvRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [createMissingCustomers, setCreateMissingCustomers] = useState(true);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [importResult, setImportResult] = useState<ImportOpportunitiesResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [authorized, setAuthorized] = useState(false);

  // Check feature flag
  useEffect(() => {
    (async () => {
      try {
        const flags = await api.getFeatureFlags();
        const hasFlag = (flags || []).some(f => f.enabled && f.key.toLowerCase() === 'sales-monday-columns');
        if (!hasFlag) {
          navigate('/opportunities');
          return;
        }
        setAuthorized(true);
      } catch {
        navigate('/opportunities');
      }
    })();
  }, [navigate]);

  const processFileData = useCallback((data: any[][], fileName: string) => {
    try {
      if (data.length < 2) {
        setError('El archivo debe tener al menos una fila de encabezados y una de datos.');
        return;
      }

      // Monday.com exports have headers not in the first row - find the header row
      let headerRowIndex = 0;
      for (let i = 0; i < Math.min(10, data.length); i++) {
        const row = data[i];
        if (row && row.length > 5) {
          const nonEmptyCount = row.filter((c: any) => c !== undefined && c !== null && c !== '').length;
          if (nonEmptyCount > 5) {
            headerRowIndex = i;
            break;
          }
        }
      }

      const headerLine = data[headerRowIndex].map((h: any) => String(h || '').trim());
      setHeaders(headerLine.filter(h => h !== ''));

      const rows: CsvRow[] = [];
      for (let i = headerRowIndex + 1; i < data.length; i++) {
        const values = data[i];
        if (!values || values.length === 0) continue;

        const row: CsvRow = {};
        let hasData = false;
        headerLine.forEach((header: string, idx: number) => {
          if (header) {
            const val = values[idx];
            row[header] = val !== undefined && val !== null ? String(val) : '';
            if (val !== undefined && val !== null && val !== '') hasData = true;
          }
        });
        if (hasData) rows.push(row);
      }

      setCsvData(rows);

      // Auto-detect mappings based on common column names
      const autoMapping: ColumnMapping = {};
      const lowerHeaders = headerLine.map((h: string) => h.toLowerCase());

      const autoMapPatterns: [keyof ColumnMapping, string[]][] = [
        // Patrones exactos de Monday.com para Ventas
        ['title', ['nombre']],
        ['tasks', ['tareas']],
        ['owner', ['resp.', 'resp']],
        ['type', ['tipo de opps']],
        ['customerName', ['cliente']],
        ['state', ['etapas']],
        ['estimatedValue', ['valor del acuerdo']],
        ['finalInvoiceAmount', ['factura final']],
        ['probabilityPct', ['probabilidad']],
        ['offerReference', ['oferta/ factura/ remito', 'oferta']],
        ['receiptNumber', ['comprobante']],
        ['estimatedCloseDate', ['fecha de cierre']],
        ['deliverySlipReference', ['remito de entrega']],
        ['deliveryCondition', ['condicion delivery']],
        ['deliveryStatus', ['estado delivery']],
        ['receivedConfirmation', ['recibido']],
        ['deliveryInfo', ['info entrega']],
        ['paymentStatus', ['pago']],
        ['paymentMethod', ['condicion pago']],
        ['isReconciled', ['conciliado']],
        ['paidAmount', ['abono ($)', 'abono']],
        ['balance', ['saldo']],
        ['actualDeliveryDate', ['fecha de entrega']],
        ['paymentDueDate', ['fecha vto']],
        ['paymentDate', ['fecha abono']],
        ['daysOverdue', ['días de atraso', 'dias de atraso']],
        ['commissionPct', ['comisión', 'comision']],
        ['commissionAmount', ['monto comisión', 'monto comision']],
        ['documentUrl', ['monday doc', 'monday doc v2']],
      ];

      for (const [field, patterns] of autoMapPatterns) {
        const matchIdx = lowerHeaders.findIndex((h: string) => patterns.some(p => h.includes(p)));
        if (matchIdx !== -1) {
          autoMapping[field] = headerLine[matchIdx];
        }
      }

      setMapping(autoMapping);
      setStep('mapping');
    } catch (err) {
      setError('Error al procesar el archivo. Verifique el formato.');
    }
  }, []);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError(null);
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as any[][];
          processFileData(jsonData, file.name);
        } catch (err) {
          setError('Error al leer el archivo Excel. Verifique que sea un archivo válido.');
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);

          const parseCSVLine = (line: string): string[] => {
            const result: string[] = [];
            let current = '';
            let inQuotes = false;

            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if ((char === ',' || char === ';') && !inQuotes) {
                result.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            result.push(current.trim());
            return result;
          };

          const jsonData = lines.map(line => parseCSVLine(line));
          processFileData(jsonData, file.name);
        } catch (err) {
          setError('Error al procesar el archivo CSV. Verifique el formato.');
        }
      };
      reader.readAsText(file);
    }
  }, [processFileData]);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      const input = document.createElement('input');
      input.type = 'file';
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      input.files = dataTransfer.files;
      handleFileUpload({ target: input } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [handleFileUpload]);

  const handleMappingChange = (field: keyof ColumnMapping, value: string) => {
    setMapping(prev => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const getMappedRows = (): ImportOpportunityRow[] => {
    return csvData.map(row => {
      // Parse date helper
      const parseDate = (field?: string): string | undefined => {
        if (!field) return undefined;
        const rawDate = row[field];
        if (!rawDate) return undefined;
        const numDate = parseFloat(rawDate);
        if (!isNaN(numDate) && numDate > 40000) {
          return excelDateToISO(numDate);
        }
        return excelDateToISO(rawDate);
      };

      // Parse numbers
      const parseNumber = (val: string | undefined): number => {
        if (!val) return 0;
        const cleaned = val.toString().replace(/[^0-9.,\-]/g, '').replace(',', '.');
        const num = parseFloat(cleaned);
        return isNaN(num) ? 0 : num;
      };

      // Parse boolean
      const parseBool = (val: string | undefined): boolean => {
        if (!val) return false;
        const lower = val.toLowerCase().trim();
        return lower === 'true' || lower === 'sí' || lower === 'si' || lower === 'yes' || lower === '1' || lower === 'x';
      };

      return {
        title: mapping.title ? row[mapping.title] : undefined,
        tasks: mapping.tasks ? row[mapping.tasks] : undefined,
        owner: mapping.owner ? row[mapping.owner] : undefined,
        type: mapping.type ? row[mapping.type] : undefined,
        customerName: mapping.customerName ? row[mapping.customerName] : undefined,
        state: mapping.state ? row[mapping.state] : undefined,
        estimatedValue: parseNumber(mapping.estimatedValue ? row[mapping.estimatedValue] : undefined),
        finalInvoiceAmount: parseNumber(mapping.finalInvoiceAmount ? row[mapping.finalInvoiceAmount] : undefined),
        probabilityPct: parseNumber(mapping.probabilityPct ? row[mapping.probabilityPct] : undefined),
        offerReference: mapping.offerReference ? row[mapping.offerReference] : undefined,
        receiptNumber: mapping.receiptNumber ? row[mapping.receiptNumber] : undefined,
        estimatedCloseDate: parseDate(mapping.estimatedCloseDate),
        deliverySlipReference: mapping.deliverySlipReference ? row[mapping.deliverySlipReference] : undefined,
        deliveryCondition: mapping.deliveryCondition ? row[mapping.deliveryCondition] : undefined,
        deliveryStatus: mapping.deliveryStatus ? row[mapping.deliveryStatus] : undefined,
        receivedConfirmation: parseBool(mapping.receivedConfirmation ? row[mapping.receivedConfirmation] : undefined),
        deliveryInfo: mapping.deliveryInfo ? row[mapping.deliveryInfo] : undefined,
        paymentStatus: mapping.paymentStatus ? row[mapping.paymentStatus] : undefined,
        paymentMethod: mapping.paymentMethod ? row[mapping.paymentMethod] : undefined,
        isReconciled: parseBool(mapping.isReconciled ? row[mapping.isReconciled] : undefined),
        paidAmount: parseNumber(mapping.paidAmount ? row[mapping.paidAmount] : undefined),
        balance: parseNumber(mapping.balance ? row[mapping.balance] : undefined),
        actualDeliveryDate: parseDate(mapping.actualDeliveryDate),
        paymentDueDate: parseDate(mapping.paymentDueDate),
        paymentDate: parseDate(mapping.paymentDate),
        daysOverdue: Math.round(parseNumber(mapping.daysOverdue ? row[mapping.daysOverdue] : undefined)),
        commissionPct: parseNumber(mapping.commissionPct ? row[mapping.commissionPct] : undefined),
        commissionAmount: parseNumber(mapping.commissionAmount ? row[mapping.commissionAmount] : undefined),
      };
    }).filter(row => row.title && row.title.trim() !== ''); // Filter out rows without title
  };

  const handleImport = async () => {
    setImporting(true);
    setError(null);
    setStep('importing');

    try {
      const rows = getMappedRows();
      const result = await api.importOpportunities({
        rows,
        createMissingCustomers,
        skipDuplicates,
      });
      setImportResult(result);
      setStep('result');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al importar oportunidades');
      setStep('preview');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setStep('upload');
    setCsvData([]);
    setHeaders([]);
    setMapping({});
    setImportResult(null);
    setError(null);
  };

  if (!authorized) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <LinearProgress sx={{ width: 200 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        Importar Oportunidades desde Monday.com
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Paso 1: Cargar archivo</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Exporta tus oportunidades desde Monday.com (Excel o CSV) y cargalas aquí. El sistema detectará automáticamente las columnas.
            </Typography>

            <Box
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 6,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 200,
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
              component="label"
            >
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                hidden
                onChange={handleFileUpload}
              />
              <CloudUpload sx={{ fontSize: 64, color: 'text.secondary', mb: 2, display: 'block' }} />
              <Typography variant="h6" color="text.secondary">
                Arrastrá un archivo Excel o CSV aquí
              </Typography>
              <Typography variant="body2" color="text.secondary">
                o hacé clic para seleccionar (.xlsx, .xls, .csv)
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Column Mapping */}
      {step === 'mapping' && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Paso 2: Mapear columnas</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Asociá cada campo con la columna correspondiente del archivo. El campo "Nombre/Titulo" es requerido.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
              {(Object.keys(fieldLabels) as (keyof ColumnMapping)[]).map((field) => (
                <TextField
                  key={field}
                  select
                  fullWidth
                  size="small"
                  label={fieldLabels[field]}
                  value={mapping[field] || ''}
                  onChange={(e) => handleMappingChange(field, e.target.value)}
                >
                  <MenuItem value="">
                    <em>No mapear</em>
                  </MenuItem>
                  {headers.map((header) => (
                    <MenuItem key={header} value={header}>{header}</MenuItem>
                  ))}
                </TextField>
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" sx={{ mb: 2 }}>Opciones de importación</Typography>
            <Stack spacing={1} sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createMissingCustomers}
                    onChange={(e) => setCreateMissingCustomers(e.target.checked)}
                  />
                }
                label="Crear clientes que no existan"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipDuplicates}
                    onChange={(e) => setSkipDuplicates(e.target.checked)}
                  />
                }
                label="Omitir duplicados (mismo ID Monday)"
              />
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={resetImport}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={() => setStep('preview')}
                disabled={!mapping.title}
              >
                Vista previa
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Preview */}
      {step === 'preview' && (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Paso 3: Vista previa</Typography>
              <Chip label={`${getMappedRows().length} oportunidades`} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Verificá que los datos se vean correctos antes de importar.
            </Typography>

            <TableContainer sx={{ maxHeight: 400, mb: 3 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Título</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Vendedor</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="right">Prob %</TableCell>
                    <TableCell>Fecha Cierre</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getMappedRows().slice(0, 50).map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.title || '-'}</TableCell>
                      <TableCell>{row.customerName || '-'}</TableCell>
                      <TableCell>{row.owner || '-'}</TableCell>
                      <TableCell>{row.state || '-'}</TableCell>
                      <TableCell align="right">${row.estimatedValue?.toLocaleString()}</TableCell>
                      <TableCell align="right">{row.probabilityPct}%</TableCell>
                      <TableCell>{row.estimatedCloseDate || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {getMappedRows().length > 50 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Mostrando 50 de {getMappedRows().length} filas
              </Typography>
            )}

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => setStep('mapping')}>
                Volver
              </Button>
              <Button
                variant="contained"
                startIcon={<Upload />}
                onClick={handleImport}
              >
                Importar {getMappedRows().length} oportunidades
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Importing */}
      {step === 'importing' && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Importando oportunidades...</Typography>
            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Esto puede tardar unos segundos dependiendo de la cantidad de datos.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Result */}
      {step === 'result' && importResult && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              {importResult.success ? (
                <Check sx={{ fontSize: 48, color: 'success.main' }} />
              ) : (
                <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />
              )}
              <Box>
                <Typography variant="h6">
                  {importResult.success ? 'Importación completada' : 'Importación con errores'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {importResult.imported} oportunidades importadas, {importResult.skipped} omitidas
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Card variant="outlined" sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="success.main">{importResult.imported}</Typography>
                <Typography variant="body2" color="text.secondary">Importadas</Typography>
              </Card>
              <Card variant="outlined" sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="warning.main">{importResult.skipped}</Typography>
                <Typography variant="body2" color="text.secondary">Omitidas</Typography>
              </Card>
              <Card variant="outlined" sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                <Typography variant="h4" color="error.main">{importResult.errors.length}</Typography>
                <Typography variant="body2" color="text.secondary">Errores</Typography>
              </Card>
            </Stack>

            {importResult.errors.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Errores:</Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                  {importResult.errors.map((err, idx) => (
                    <Typography key={idx} variant="body2" color="error.main" sx={{ mb: 0.5 }}>
                      {err}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={resetImport}>
                Importar otro archivo
              </Button>
              <Button variant="contained" href="/opportunities">
                Ver oportunidades
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
